/**
 * The theme registry: adding a theme means adding one JSON file here and one
 * line to THEMES (SPEC §6). Every theme must define every key in
 * theme.schema.json; a parity test enforces it.
 */
// Explicit extensions and JSON import attributes: this module is also loaded by
// build tooling (vite.config → content plugin), which uses native ESM rules.
import drafting from "./drafting.light.json" with { type: "json" };
import ember from "./ember.dark.json" with { type: "json" };

export type UiToken =
  | "bg.canvas"
  | "bg.panel"
  | "bg.panelRaised"
  | "bg.field"
  | "border"
  | "text.primary"
  | "text.secondary"
  | "text.tertiary"
  | "accent"
  | "accent.soft"
  | "accent.on"
  | "primary"
  | "primary.on"
  | "danger"
  | "success"
  | "overlay.scrim"
  | "overlay.hud"
  | "statusbar.playing.bg"
  | "statusbar.playing.text";

/** Colors consumed by the render backend and viewport overlays (not CSS chrome). */
export interface ViewportTokens {
  readonly sky: readonly [string, string, string];
  readonly floor: string;
  readonly grid: { readonly color: string; readonly opacity: number };
  readonly keyLightTint: string;
  readonly vfx: string;
  readonly selection: string;
  readonly "selection.on": string;
  readonly "label.bg": string;
  readonly "label.text": string;
  readonly wireframe: string;
  readonly "worldUi.border": string;
  readonly "worldUi.text": string;
  readonly "worldUi.bg": string;
  readonly "gizmo.center": string;
  readonly "gizmo.plane": string;
}

export interface Theme {
  readonly id: string;
  readonly name: string;
  readonly mode: "dark" | "light";
  readonly font: { readonly sans: string; readonly mono: string };
  readonly ui: Readonly<Record<UiToken, string>>;
  readonly axis: { readonly x: string; readonly y: string; readonly z: string };
  readonly viewport: ViewportTokens;
}

/**
 * JSON imports widen literal types (`mode` becomes string, `sky` becomes string[]).
 * The shape is guaranteed by the schema and parity tests in theme.test.ts,
 * so this is the one place the JSON is treated as a Theme.
 */
function asTheme(json: unknown): Theme {
  return json as Theme;
}

/** All shipped themes. The first dark and first light theme are the defaults. */
export const THEMES: readonly Theme[] = [asTheme(ember), asTheme(drafting)];

export function findTheme(id: string): Theme | undefined {
  return THEMES.find((theme) => theme.id === id);
}

/** The default theme for an OS color scheme (first visit follows `prefers-color-scheme`). */
export function defaultThemeFor(mode: "dark" | "light"): Theme {
  const theme = THEMES.find((t) => t.mode === mode) ?? THEMES[0];
  if (!theme) throw new Error("No themes registered");
  return theme;
}
